// TMOS Create Action Modal
// Date: December 9, 2025
// Purpose: Modal form for creating new actions
//
// Features:
// - Create standalone actions
// - Create actions with context (Risk, Issue, Project, etc.)
// - Assign to users
// - Set priority, due date, category
// - Use templates (if available)

import React, { useState, useEffect, useRef } from 'react';
import { actionService, CreateActionRequest, ActionPriority } from '@/services/actionService';
import { actionTemplateService, ActionTemplate } from '@/services/actionTemplateService';
import { useLanguage } from '@/state/language/LanguageProvider';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import api from '@/services/api';
import { useAuth } from '@/state/auth/AuthProvider';

interface CreateActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  contextType?: string; // Pre-fill context if creating from entity page
  contextId?: string; // Pre-fill context if creating from entity page
}

/**
 * CreateActionModal Component
 * 
 * Modal form for creating new actions with all options:
 * - Title and description
 * - Priority selection
 * - Due date
 * - Activity category
 * - User assignment
 * - Context association (auto-filled if provided)
 * 
 * Business Rules:
 * - Title is required (max 500 characters)
 * - Priority defaults to MEDIUM
 * - Status defaults to PENDING
 * - Can assign to multiple users
 * - Can associate with multiple contexts
 */
export const CreateActionModal: React.FC<CreateActionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  contextType,
  contextId
}) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState<ActionPriority>('MEDIUM');
  const [dueDate, setDueDate] = useState('');
  const [activityCategoryId, setActivityCategoryId] = useState<string>('');
  const [assignedUserIds, setAssignedUserIds] = useState<string[]>([]);
  
  // Available users for assignment
  const [availableUsers, setAvailableUsers] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const userSearchLoading = false; // Search is synchronous, no loading state needed
  const [showUserSearch, setShowUserSearch] = useState(false);
  const userSearchRef = useRef<HTMLDivElement>(null);
  const [activityCategories, setActivityCategories] = useState<Array<{ id: string; name: string }>>([]);
  const [templates, setTemplates] = useState<ActionTemplate[]>([]);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const { user: currentUser } = useAuth();

  // Load data on mount
  useEffect(() => {
    if (isOpen) {
      loadFormData();
    } else {
      // Reset form when closed
      resetForm();
    }
  }, [isOpen]);

  /**
   * Load users, categories, and templates for form
   */
  const loadFormData = async () => {
    try {
      // Set current user as default assignee
      if (currentUser?.id) {
        setAssignedUserIds([currentUser.id]);
      }
      
      // Load users from API
      try {
        const usersResponse = await api.get('/users/admin/users', {
          params: { limit: 1000 } // Get all users in tenant
        });
        
        if (usersResponse.data.success) {
          const users = usersResponse.data.data?.users || usersResponse.data.data || [];
          // Filter to current tenant only (unless platform admin)
          const filteredUsers = users
            .filter((u: any) => !currentUser?.tenantId || u.tenant_id === currentUser.tenantId)
            .map((u: any) => ({
              id: u.id,
              name: `${u.firstName || u.first_name || ''} ${u.lastName || u.last_name || ''}`.trim() || u.email || 'Unknown User',
              email: u.email || ''
            }));
          
          // Ensure current user is always in the list (even if not returned by API)
          if (currentUser?.id) {
            const currentUserInList = filteredUsers.find((u: any) => u.id === currentUser.id);
            if (!currentUserInList) {
              filteredUsers.unshift({
                id: currentUser.id,
                name: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || currentUser.email || 'You',
                email: currentUser.email || ''
              });
            }
          }
          
          setAvailableUsers(filteredUsers);
        } else {
          // If API call succeeded but returned no success, ensure current user is in list
          if (currentUser?.id) {
            setAvailableUsers([{
              id: currentUser.id,
              name: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || currentUser.email || 'You',
              email: currentUser.email || ''
            }]);
          }
        }
      } catch (err) {
        console.error('Error loading users:', err);
        // Even if API fails, ensure current user is available
        if (currentUser?.id) {
          setAvailableUsers([{
            id: currentUser.id,
            name: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || currentUser.email || 'You',
            email: currentUser.email || ''
          }]);
        }
      }
      
      // Load activity categories from API
      try {
        const categoriesResponse = await api.get('/activity-categories');
        if (categoriesResponse.data.success) {
          const categories = categoriesResponse.data.data || [];
          setActivityCategories(categories.map((c: any) => ({
            id: c.id,
            name: c.name
          })));
        }
      } catch (err) {
        console.error('Error loading activity categories:', err);
        // Continue even if categories fail to load
      }
      
      // Load templates for the context type (if provided)
      if (contextType) {
        const templateList = await actionTemplateService.getTemplates(contextType);
        setTemplates(templateList);
      }
    } catch (err) {
      console.error('Error loading form data:', err);
    }
  };

  /**
   * Handle template selection
   */
  const handleTemplateSelect = async (templateId: string) => {
    try {
      // Apply template - create actions from template
      await actionTemplateService.applyTemplate(
        templateId,
        contextType,
        contextId
      );
      
      resetForm();
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error applying template:', err);
      setError(err.message || t('actions.templateApplyError') || 'Failed to apply template');
    }
  };

  /**
   * Reset form to initial state
   */
  const resetForm = () => {
    setTitle('');
    setPriority('MEDIUM');
    setDueDate('');
    setActivityCategoryId('');
    // Set current user as default assignee
    setAssignedUserIds(currentUser?.id ? [currentUser.id] : []);
    setError(null);
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      setError(t('actions.titleRequired') || 'Title is required');
      return;
    }

    if (title.length > 500) {
      setError(t('actions.titleTooLong') || 'Title must be 500 characters or less');
      return;
    }

    // Validate that at least one user is assigned (should always have current user, but check anyway)
    if (assignedUserIds.length === 0) {
      // If no users selected, default to current user
      if (currentUser?.id) {
        setAssignedUserIds([currentUser.id]);
      } else {
        setError(t('actions.assignToRequired') || 'Please assign this action to at least one user');
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const actionData: CreateActionRequest = {
        title: title.trim(),
        priority,
        activity_category_id: activityCategoryId || undefined,
        due_date: dueDate ? new Date(dueDate).toISOString() : undefined,
        context_type: contextType,
        context_id: contextId,
        assigned_user_ids: assignedUserIds.length > 0 ? assignedUserIds : undefined
      };

      await actionService.createAction(actionData);
      
      resetForm();
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error creating action:', err);
      setError(err.message || t('actions.createError') || 'Failed to create action');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Search users for assignment (filter from already loaded users)
   */
  const searchUsers = (query: string) => {
    if (!query || query.length < 1) {
      // If no query, show all users except already assigned (but always show current user)
      setUserSearchResults(availableUsers.filter((u: any) => 
        !assignedUserIds.includes(u.id) || u.id === currentUser?.id
      ));
      return;
    }
    
    // Filter loaded users by search query (name or email)
    // Always include current user if they match the search, even if already assigned
    const queryLower = query.toLowerCase();
    const filtered = availableUsers.filter((u: any) => {
      const matchesSearch = u.name.toLowerCase().includes(queryLower) || u.email.toLowerCase().includes(queryLower);
      const isCurrentUser = u.id === currentUser?.id;
      // Show if: (not assigned OR is current user) AND matches search
      return matchesSearch && (!assignedUserIds.includes(u.id) || isCurrentUser);
    });
    setUserSearchResults(filtered);
  };

  /**
   * Handle user search input change
   */
  useEffect(() => {
    if (showUserSearch && availableUsers.length > 0) {
      searchUsers(userSearchTerm);
    }
  }, [userSearchTerm, showUserSearch, availableUsers, assignedUserIds]);

  /**
   * Load users when search is opened
   */
  useEffect(() => {
    if (showUserSearch && availableUsers.length === 0) {
      loadFormData();
    } else if (showUserSearch) {
      searchUsers(userSearchTerm);
    }
  }, [showUserSearch]);

  /**
   * Handle assigning user to action
   */
  const handleAssignUser = (userId: string) => {
    if (!assignedUserIds.includes(userId)) {
      setAssignedUserIds(prev => [...prev, userId]);
      setUserSearchTerm('');
      setShowUserSearch(false);
    }
  };

  /**
   * Handle removing assigned user
   */
  const handleUnassignUser = (userId: string) => {
    setAssignedUserIds(prev => prev.filter(id => id !== userId));
  };

  /**
   * Close user search dropdown when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userSearchRef.current && !userSearchRef.current.contains(event.target as Node)) {
        setShowUserSearch(false);
      }
    };

    if (showUserSearch) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserSearch]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-[var(--color-surface-bg)] rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-[var(--color-surface-bg)] border-b border-[var(--color-border)] px-6 py-4 flex items-center justify-between z-10">
            <h2 className="text-xl font-semibold text-[var(--color-surface-text)]">
              {t('actions.createAction') || 'Create Action'}
            </h2>
            <button
              onClick={onClose}
              className="text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)]"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Error Message */}
            {error && (
              <div className="p-3 bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 rounded-md text-[var(--color-error)] text-sm">
                {error}
              </div>
            )}

            {/* Context Info (if provided) - Read-only display */}
            {contextType && contextId && (
              <div className="p-3 bg-[var(--color-info)]/10 border border-[var(--color-info)]/30 rounded-md">
                <div className="text-xs font-medium text-[var(--color-info)] mb-1">
                  {t('actions.context') || 'Context'}
                </div>
                <div className="text-sm text-[var(--color-info)] capitalize">
                  {contextType.replace(/_/g, ' ')} • {contextId.substring(0, 8)}...
                </div>
              </div>
            )}

            {/* Template Selector */}
            {templates.length > 0 && (
              <div>
                <button
                  type="button"
                  onClick={() => setShowTemplateSelector(!showTemplateSelector)}
                  className="w-full px-4 py-2 border border-[var(--color-info)]/40 bg-[var(--color-info)]/10 text-[var(--color-info)] rounded-md text-sm hover:bg-[var(--color-info)]/20"
                >
                  {t('actions.useTemplate') || 'Use Template'} ({templates.length} {t('actions.available') || 'available'})
                </button>
                
                {showTemplateSelector && (
                  <div className="mt-2 border border-[var(--color-border)] rounded-md p-3 bg-[var(--color-secondary-bg)] max-h-40 overflow-y-auto">
                    {templates.map(template => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => handleTemplateSelect(template.id)}
                        className="w-full text-left px-3 py-2 hover:bg-[var(--color-surface-bg)] rounded text-sm mb-1 text-[var(--color-surface-text)]"
                      >
                        <div className="font-medium">{template.name}</div>
                        {template.description && (
                          <div className="text-xs text-[var(--color-secondary-text)]">{template.description}</div>
                        )}
                        <div className="text-xs text-[var(--color-secondary-text)]">
                          {template.template_data.length} {t('actions.actions') || 'actions'}
                          {template.is_platform_template && ' • Platform'}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                {t('actions.title') || 'Title'} <span className="text-[var(--color-error)]">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={500}
                required
                className="w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                placeholder={t('actions.titlePlaceholder') || 'Enter action title...'}
              />
              <div className="text-xs text-[var(--color-secondary-text)] mt-1">
                {title.length}/500 {t('actions.characters') || 'characters'}
              </div>
            </div>

            {/* Priority and Due Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                  {t('actions.priority.label') || 'Priority'}
                </label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as ActionPriority)}
                  className="w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                >
                  <option value="LOW">{t('actions.priority.low') || 'Low'}</option>
                  <option value="MEDIUM">{t('actions.priority.medium') || 'Medium'}</option>
                  <option value="HIGH">{t('actions.priority.high') || 'High'}</option>
                  <option value="CRITICAL">{t('actions.priority.critical') || 'Critical'}</option>
                </select>
              </div>

              {/* Due Date */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                  {t('actions.dueDate') || 'Due Date'}
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                />
              </div>
            </div>

            {/* Activity Category */}
            {activityCategories.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                  {t('actions.category') || 'Category'}
                </label>
                <select
                  value={activityCategoryId}
                  onChange={(e) => setActivityCategoryId(e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                >
                  <option value="">{t('actions.noCategory') || 'No Category'}</option>
                  {activityCategories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Assign To Users - ALWAYS VISIBLE (required for actions) */}
            <div>
              <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                {t('actions.assignTo') || 'Assign To'} <span className="text-[var(--color-error)]">*</span>
              </label>
              
              {/* Assigned Users List */}
              {assignedUserIds.length > 0 && (
                <div className="mb-2 space-y-1">
                  {assignedUserIds.map(userId => {
                    const user = availableUsers.find(u => u.id === userId);
                    return user ? (
                      <div key={userId} className="flex items-center justify-between p-2 bg-[var(--color-secondary-bg)] rounded text-sm">
                        <span className="text-[var(--color-surface-text)]">{user.name} {user.email && <span className="text-[var(--color-secondary-text)]">({user.email})</span>}</span>
                        <button
                          type="button"
                          onClick={() => handleUnassignUser(userId)}
                          className="text-[var(--color-error)] hover:opacity-80"
                          title={t('actions.unassign') || 'Unassign'}
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ) : null;
                  })}
                </div>
              )}

              {/* User Search */}
              <div ref={userSearchRef} className="relative">
                <button
                  type="button"
                  onClick={() => setShowUserSearch(!showUserSearch)}
                  className="w-full flex items-center justify-between px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface-bg)] rounded-md text-sm text-left hover:bg-[var(--color-secondary-bg)]"
                >
                  <span className="text-[var(--color-secondary-text)]">{t('actions.searchUsers') || 'Search users to assign...'}</span>
                  <PlusIcon className="w-4 h-4 text-[var(--color-secondary-text)]" />
                </button>
                
                {showUserSearch && (
                  <div className="absolute z-10 w-full mt-1 bg-[var(--color-surface-bg)] border border-[var(--color-border)] rounded-md shadow-lg">
                    <input
                      type="text"
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                      placeholder={t('actions.searchUsers') || 'Search by name or email...'}
                      className="w-full px-3 py-2 border-b border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] rounded-t-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                      autoFocus
                    />
                    {userSearchLoading && (
                      <div className="px-3 py-2 text-sm text-[var(--color-secondary-text)]">
                        {t('actions.loading') || 'Loading...'}
                      </div>
                    )}
                    {!userSearchLoading && userSearchResults.length > 0 && (
                      <div className="max-h-60 overflow-y-auto">
                        {userSearchResults.map((user) => (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => handleAssignUser(user.id)}
                            className="w-full text-left px-3 py-2 hover:bg-[var(--color-secondary-bg)] text-sm text-[var(--color-surface-text)]"
                          >
                            <div className="font-medium">{user.name}</div>
                            {user.email && (
                              <div className="text-xs text-[var(--color-secondary-text)]">{user.email}</div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                    {!userSearchLoading && userSearchTerm && userSearchResults.length === 0 && (
                      <div className="px-3 py-2 text-sm text-[var(--color-secondary-text)]">
                        {t('actions.noUsersFound') || 'No users found'}
                      </div>
                    )}
                    {!userSearchLoading && !userSearchTerm && userSearchResults.length === 0 && availableUsers.length > 0 && (
                      <div className="px-3 py-2 text-sm text-[var(--color-secondary-text)]">
                        {t('actions.typeToSearch') || 'Type to search users...'}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {assignedUserIds.length === 0 && (
                <div className="text-xs text-amber-600 mt-1">
                  {t('actions.assignToWarning') || 'Please assign this action to at least one user'}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-[var(--color-border)]">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-[var(--color-border)] rounded-md text-sm text-[var(--color-surface-text)] hover:bg-[var(--color-secondary-bg)]"
                disabled={loading}
              >
                {t('actions.cancel') || 'Cancel'}
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-[var(--color-primary-bg)] text-[var(--color-secondary-font)] rounded-md text-sm hover:opacity-90 disabled:opacity-50"
                disabled={loading || !title.trim()}
              >
                {loading ? (t('actions.creating') || 'Creating...') : (t('actions.create') || 'Create Action')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

